// src/pages/Dashboard/hooks/useDashboardCases.js

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Hook customizado para gerenciar casos no Dashboard
 * Encapsula toda a lógica de carregamento, seleção e criação de casos
 * 
 * @returns {object} - Estado e funções para gerenciar casos
 */
export const useDashboardCases = () => {
  const { user } = useAuth();
  const [casos, setCasos] = useState([]);
  const [casoSelecionado, setCasoSelecionado] = useState(null);
  const [loadingCasos, setLoadingCasos] = useState(true);
  const [criandoCaso, setCriandoCaso] = useState(false);

  /**
   * Carrega a lista de casos recentes do usuário
   */
  const carregarCasos = useCallback(async () => {
    try {
      setLoadingCasos(true);
      const response = await axios.get('/api/casos/recentes');
      if (response.data.success) {
        console.log('✅ Casos carregados:', response.data.data);
        setCasos(response.data.data);
        
        // Se não há caso selecionado e há casos disponíveis, selecionar o primeiro
        if (response.data.data.length > 0 && !casoSelecionado) {
          const primeiroCaso = response.data.data[0];
          console.log('✅ Primeiro caso selecionado:', primeiroCaso);
          console.log('✅ dados_cliente do primeiro caso:', primeiroCaso.dados_cliente);
          setCasoSelecionado(primeiroCaso);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar casos:', error);
    } finally {
      setLoadingCasos(false);
    }
  }, [casoSelecionado]);

  /**
   * Seleciona um caso específico
   * @param {object} caso - Caso a ser selecionado
   */
  const selecionarCaso = useCallback((caso) => {
    console.log('🔄 Selecionando caso:', caso);
    console.log('🔍 dados_cliente do caso selecionado:', caso.dados_cliente);
    setCasoSelecionado(caso);
  }, []);

  /**
   * Cria um novo caso
   * @param {object} dadosCaso - Dados do novo caso (name, cod_cli, description)
   * @returns {Promise<boolean>} - true se criado com sucesso
   */
  const criarNovoCaso = useCallback(async (dadosCaso) => {
    const { nomeCaso, codigoCliente, descricaoCaso } = dadosCaso;

    if (!nomeCaso.trim() || !descricaoCaso.trim()) {
      throw new Error('Nome e descrição são obrigatórios');
    }

    setCriandoCaso(true);
    try {
      const response = await axios.post('/api/casos/criar', {
        name: nomeCaso,
        cod_cli: codigoCliente || null,
        description: descricaoCaso,
        user_email: user.email
      });

      if (response.data.success) {
        await carregarCasos();
        console.log('✅ Caso criado com sucesso');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Erro ao criar caso:', error);
      throw error;
    } finally {
      setCriandoCaso(false);
    }
  }, [user, carregarCasos]);

  /**
   * Reseta o caso selecionado (útil para limpar estado)
   */
  const resetarCasoSelecionado = useCallback(() => {
    setCasoSelecionado(null);
  }, []);

  // Carregar casos ao montar o componente
  useEffect(() => {
    if (user) {
      carregarCasos();
    }
  }, [user, carregarCasos]);

  return {
    // Estado
    casos,
    casoSelecionado,
    loadingCasos,
    criandoCaso,
    
    // Funções
    carregarCasos,
    selecionarCaso,
    criarNovoCaso,
    resetarCasoSelecionado
  };
};